import React, { useState, useEffect } from 'react';
import { ChevronDown, Shield, Flame, Heart, HardHat, Wrench } from 'lucide-react';

const FAQItem = ({ question, answer, isOpen, onToggle, iconColor }) => (
  <div className="border-b border-slate-200 last:border-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 text-left group"
      aria-expanded={isOpen}
    >
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${iconColor}`}></div>
        <span className="font-semibold text-secondary group-hover:text-slate-700 transition-colors pr-4">
          {question}
        </span>
      </div>
      <ChevronDown 
        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    <div 
      className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}
    >
      <p className="text-slate-600 leading-relaxed pl-5">{answer}</p>
    </div>
  </div>
);

const FirstResponderFAQ = ({ 
  title = "First Responder Injury FAQs",
  subtitle,
  faqs,
  schemaId = "first-responder-faq"
}) => {
  const [openIndex, setOpenIndex] = useState(0);

  // Rotate through all 5 sector colors for visual interest
  const getIconColor = (index) => {
    const colors = [
      'bg-blue-600',    // Law Enforcement
      'bg-red-600',     // Fire & Rescue
      'bg-emerald-600', // Emergency Medical
      'bg-amber-600',   // Transportation/WSDOT
      'bg-slate-600',   // Public Works
    ];
    return colors[index % 5];
  };

  // Add FAQ Schema for SEO
  useEffect(() => {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const existingScript = document.getElementById(`faq-schema-${schemaId}`);
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = `faq-schema-${schemaId}`;
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(`faq-schema-${schemaId}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [faqs, schemaId]);

  return (
    <section className="py-16 bg-slate-50" data-testid="first-responder-faq">
      <div className="container mx-auto px-4 md:px-6 max-w-4xl">
        <div className="text-center mb-10">
          {/* Five-sector badge representing all public safety workers */}
          <div className="inline-flex flex-wrap items-center justify-center gap-2 md:gap-3 bg-white px-4 md:px-5 py-3 rounded-full shadow-sm border border-slate-200 mb-4">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-600">Police</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300"></div>
            <div className="flex items-center gap-1">
              <Flame className="w-4 h-4 text-red-600" />
              <span className="text-xs font-medium text-red-600">Fire</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300"></div>
            <div className="flex items-center gap-1">
              <Heart className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-medium text-emerald-600">EMS</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300"></div>
            <div className="flex items-center gap-1">
              <HardHat className="w-4 h-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-600">WSDOT</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300"></div>
            <div className="flex items-center gap-1">
              <Wrench className="w-4 h-4 text-slate-600" />
              <span className="text-xs font-medium text-slate-600">Public Works</span>
            </div>
          </div>
          
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
            {title}
          </h2>
          {subtitle && (
            <p className="text-slate-600 max-w-2xl mx-auto">{subtitle}</p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          {/* Subtle top border with five colors */}
          <div className="flex h-1 -mt-6 md:-mt-8 mb-6 rounded-t-2xl overflow-hidden">
            <div className="flex-1 bg-blue-600"></div>
            <div className="flex-1 bg-red-600"></div>
            <div className="flex-1 bg-emerald-600"></div>
            <div className="flex-1 bg-amber-600"></div>
            <div className="flex-1 bg-slate-600"></div>
          </div>
          
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
              iconColor={getIconColor(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FirstResponderFAQ;
